import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Variant-aware content region for the product page.
 *
 * Wraps a chunk of server-rendered product content (description, standards,
 * downloads) that depends on the selected variant via **metafields** — data
 * that isn't carried in the variant JSON, so it can't be updated from the
 * `variant:update` event payload alone (the way SKU/price do).
 *
 * On a single-product PDP a variant change doesn't re-render the section — the
 * variant picker only morphs itself. But it DOES fetch the full product page
 * for the newly-selected variant and hands the parsed document to us on the
 * event as `event.detail.data.html`. We find our own counterpart in that
 * document by `id` and morph it in, so the region becomes server-rendered-
 * correct for the new variant — with no extra request (we reuse the picker's).
 *
 * Each region needs a stable, unique `id` (built from the block id) that is
 * identical in the live DOM and the fetched HTML.
 */
class VariantContent extends HTMLElement {
  /** @type {Element | Document | null} */
  #container = null;

  connectedCallback() {
    // Same ancestor the SKU/price components bind to — the bubbling
    // variant:update event reaches it. `data-scope="document"` is for regions
    // in a separate section below the product information (similar products):
    // the event still bubbles up to the document. Card swatch and quick-add
    // fetches return other sections, so they carry no counterpart and no-op.
    this.#container =
      this.dataset.scope === 'document'
        ? document
        : this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    this.#container?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#container?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const html = event.detail.data?.html;
    if (!html || !this.id) return;

    // The fetched document carries the same block, same id. If it isn't there
    // (e.g. a quick-add fetch that renders a different section), do nothing.
    const next = html.getElementById(this.id);
    if (next) morph(this, next);
  };
}

if (!customElements.get('variant-content')) {
  customElements.define('variant-content', VariantContent);
}
